import { useEffect, useRef, useState } from "react";
import { http } from "../api";
import { MessageSquare, Send, Users } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

// Web Audio chime — single sine ping, no external file
const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.35);
  } catch { /* ignore */ }
};

export default function BoothChat({ user }) {
  const [booths, setBooths] = useState([]);
  const [activeBooth, setActiveBooth] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState({ total: 0, per_booth: {} });
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastCountRef = useRef(0);

  const loadBooths = () => http.get("/chat/booths").then((r) => setBooths(r.data));
  const loadUnread = () => http.get("/chat/unread").then((r) => {
    const total = r.data.total || 0;
    if (total > lastCountRef.current && lastCountRef.current !== 0) {
      playChime();
    }
    lastCountRef.current = total;
    setUnread(r.data);
  });

  useEffect(() => {
    loadBooths().then(() => setActiveBooth((cur) => cur || (booths[0]?.booth)));
    loadUnread();
  }, []);

  useEffect(() => {
    if (!activeBooth && booths.length) setActiveBooth(booths[0].booth);
  }, [booths, activeBooth]);

  const load = async (booth) => {
    if (!booth) return;
    const r = await http.get(`/chat/${booth}`);
    setMessages(r.data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 100);
  };

  useEffect(() => {
    if (!activeBooth) return;
    load(activeBooth);
    // mark this booth as read when opening
    http.post("/chat/mark-read", { booth: activeBooth }).then(loadUnread);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      await load(activeBooth);
      await http.post("/chat/mark-read", { booth: activeBooth });
      loadUnread();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeBooth]);

  const send = async () => {
    if (!text.trim() || !activeBooth) return;
    setSending(true);
    try {
      await http.post("/chat/send", { booth: activeBooth, text: text.trim() });
      setText("");
      load(activeBooth);
    } finally { setSending(false); }
  };

  const roleColor = (r) => ({ "Super Admin": "red", "Admin": "amber", "Member": "blue" }[r] || "blue");

  return (
    <>
      <PageHeader eyebrow="TEAM COORDINATION" title="Booth committee chat"
        subtitle={`One dedicated room per booth · polls every 5 seconds${unread.total ? ` · ${unread.total} unread` : ""}`} />
      <div className="chat-grid">
        <aside className="chat-sidebar panel">
          <div className="panel-head" style={{ padding: "18px 18px 12px" }}><h2>Booths</h2><p>{booths.length} rooms</p></div>
          {booths.map((b, i) => {
            const un = unread.per_booth?.[b.booth] || 0;
            return (
              <button key={b.booth} className={`chat-booth ${activeBooth === b.booth ? "active" : ""}`}
                data-testid={`chat-booth-${i}`} onClick={() => setActiveBooth(b.booth)}>
                <div><b>Booth {b.booth}</b><small>{b.count || 0} messages</small></div>
                {un > 0 && <span className="unread-badge" data-testid={`unread-badge-${b.booth}`}>{un}</span>}
              </button>
            );
          })}
        </aside>

        <section className="chat-main panel">
          {!activeBooth ? <EmptyState icon={MessageSquare} title="Pick a booth" hint="Select a booth to start chatting." /> : (
            <>
              <div className="chat-head">
                <div>
                  <b>Booth {activeBooth} committee</b>
                  <small>Signed in as {user?.name} · {user?.role}</small>
                </div>
                <span className="chip green" data-testid="chat-live-indicator"><Users size={12} /> live</span>
              </div>
              <div className="chat-body" ref={scrollRef} data-testid="chat-messages">
                {messages.length === 0 ? (
                  <p className="hint" style={{ padding: 40, textAlign: "center" }}>No messages yet — say hello.</p>
                ) : messages.map((m, i) => {
                  const mine = m.author_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`chat-msg ${mine ? "mine" : ""}`} data-testid={`chat-msg-${i}`}>
                      {!mine && <span className="avatar person-avatar small">{m.author_name.split(" ").map((x) => x[0]).join("")}</span>}
                      <div className="chat-bubble">
                        {!mine && <div className="chat-meta"><b>{m.author_name}</b><span className={`chip ${roleColor(m.author_role)}`}>{m.author_role}</span></div>}
                        <p>{m.text}</p>
                        <small>{new Date(m.created_at).toLocaleTimeString()}</small>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="chat-input">
                <input data-testid="chat-input" placeholder={`Message #booth-${activeBooth}`} value={text}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  onChange={(e) => setText(e.target.value)} />
                <button className="primary" data-testid="chat-send" disabled={sending || !text.trim()} onClick={send}>
                  <Send size={15} /> Send
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}
